import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Folder, File as FileIcon, Upload, FolderPlus, FolderUp, Download, Trash2,
  ChevronRight, Image as ImageIcon, Film, X, HardDrive, Home as HomeIcon, Loader2, FolderTree, Search, Tag, AlertTriangle, CheckCircle2,
} from 'lucide-react';
import { fmtDurata } from '../lib/produzione';
import api from '../api';
import { useToast } from '../context/ToastContext';
import { PageHeader, EmptyState, ListSkeleton } from '../components/ui';
import ClientePicker from '../components/ClientePicker';

const MAX_BYTES = 2 * 1024 * 1024 * 1024; // 2 GB
const isImg = (n) => /\.(png|jpe?g|gif|webp|svg|bmp|avif)$/i.test(n);
const isVid = (n) => /\.(mp4|mov|webm|m4v|avi)$/i.test(n);

function fmtSize(b) {
  if (!b) return '—';
  const u = ['B', 'KB', 'MB', 'GB', 'TB'];
  let i = 0, n = b;
  while (n >= 1024 && i < u.length - 1) { n /= 1024; i++; }
  return `${n.toFixed(n < 10 && i > 0 ? 1 : 0)} ${u[i]}`;
}
const uid = () => Math.random().toString(36).slice(2) + Date.now().toString(36);

export default function ClientVault() {
  const { success, error } = useToast();
  const [searchParams] = useSearchParams();
  const [clienti, setClienti] = useState([]);
  const [cliente, setCliente] = useState(searchParams.get('cliente') || '');
  const [path, setPath] = useState('');            // percorso corrente relativo
  const [entries, setEntries] = useState([]);
  const [usedBytes, setUsedBytes] = useState(0);
  const [loading, setLoading] = useState(false);
  const [uploads, setUploads] = useState([]);       // upload in corso/terminati (optimistic)
  const [dragOver, setDragOver] = useState(false);
  const [preview, setPreview] = useState(null);     // entry del file in anteprima (+ video)
  const [cerca, setCerca] = useState('');           // testo di ricerca
  const [tagSel, setTagSel] = useState('');         // filtro per tag
  const [tagsCliente, setTagsCliente] = useState([]);
  const [risultati, setRisultati] = useState(null); // null = navigazione cartelle
  const fileInput = useRef(null);
  const dirInput = useRef(null);

  useEffect(() => {
    api.get('/clienti').then(r => {
      setClienti(r.data || []);
      if (r.data && r.data[0]) setCliente(c => c || r.data[0].id);
    }).catch(() => {});
  }, []);

  const load = useCallback(() => {
    if (!cliente) { setEntries([]); return; }
    setLoading(true);
    api.get(`/vault/${cliente}/list`, { params: { path } })
      .then(r => { setEntries(r.data.entries || []); setUsedBytes(r.data.used_bytes || 0); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [cliente, path]);

  const loadTags = useCallback(() => {
    if (!cliente) return;
    api.get(`/vault/${cliente}/tags`, { silent: true }).then(r => setTagsCliente(r.data || [])).catch(() => {});
  }, [cliente]);

  useEffect(() => { setPath(''); setCerca(''); setTagSel(''); setRisultati(null); }, [cliente]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadTags(); }, [loadTags]);

  // Ricerca per testo/tag (debounce): sostituisce la vista cartelle finché è attiva
  useEffect(() => {
    if (!cliente || (!cerca.trim() && !tagSel)) { setRisultati(null); return undefined; }
    const t = setTimeout(() => {
      api.get(`/vault/${cliente}/cerca`, { params: { q: cerca.trim() || undefined, tag: tagSel || undefined }, silent: true })
        .then(r => setRisultati(r.data.entries || [])).catch(() => setRisultati([]));
    }, 250);
    return () => clearTimeout(t);
  }, [cliente, cerca, tagSel]);

  const creaStruttura = async () => {
    try {
      const r = await api.post(`/vault/${cliente}/struttura`);
      success(r.data.create?.length ? `Create: ${r.data.create.join(', ')}` : 'La struttura standard esiste già');
      setPath(''); load();
    } catch { /* toast globale */ }
  };

  const onTagsSaved = (en) => {
    const upd = (list) => list && list.map(x => x.path === en.path ? { ...x, tags: en.tags, note: en.note } : x);
    setEntries(upd); setRisultati(upd); setPreview(p => p && { ...p, tags: en.tags, note: en.note });
    loadTags();
  };

  const enterFolder = (name) => setPath(p => (p ? `${p}/${name}` : name));
  const goTo = (idx) => {
    if (idx < 0) return setPath('');
    setPath(path.split('/').slice(0, idx + 1).join('/'));
  };

  // ── Upload (optimistic + progress) ─────────────────────────────────────
  const doUpload = async (fileList) => {
    if (!cliente) { error('Seleziona un cliente'); return; }
    const files = Array.from(fileList);
    const tooBig = files.filter(f => f.size > MAX_BYTES);
    if (tooBig.length) error(`${tooBig.length} file oltre 2GB: saltati`);
    const ok = files.filter(f => f.size <= MAX_BYTES);
    if (!ok.length) return;

    const jobs = ok.map(f => ({
      id: uid(), file: f,
      relpath: f.webkitRelativePath || '',
      name: f.webkitRelativePath || f.name,
      pct: 0, status: 'uploading',
    }));
    setUploads(prev => [...jobs, ...prev]);

    // upload in parallelo limitato (3 alla volta)
    const queue = [...jobs];
    const worker = async () => {
      while (queue.length) {
        const j = queue.shift();
        const form = new FormData();
        form.append('path', path);
        if (j.relpath) form.append('relpath', j.relpath);
        form.append('file', j.file);
        try {
          await api.post(`/vault/${cliente}/upload`, form, {
            headers: { 'Content-Type': 'multipart/form-data' }, silent: true,
            onUploadProgress: e => {
              const pct = Math.round((e.loaded / (e.total || j.file.size)) * 100);
              setUploads(prev => prev.map(x => x.id === j.id ? { ...x, pct } : x));
            },
          });
          setUploads(prev => prev.map(x => x.id === j.id ? { ...x, pct: 100, status: 'done' } : x));
        } catch {
          setUploads(prev => prev.map(x => x.id === j.id ? { ...x, status: 'error' } : x));
        }
      }
    };
    await Promise.all([worker(), worker(), worker()]);
    load();
    const done = jobs.filter(j => j.status !== 'error').length;
    if (done) success(`${jobs.length} file caricati`);
    setTimeout(() => setUploads(prev => prev.filter(x => x.status === 'uploading')), 2000);
  };

  const onDrop = (e) => {
    e.preventDefault(); setDragOver(false);
    if (e.dataTransfer?.files?.length) doUpload(e.dataTransfer.files);
  };

  const newFolder = async () => {
    const name = window.prompt('Nome della nuova cartella:');
    if (!name) return;
    // optimistic
    setEntries(prev => [{ name, is_dir: true, size: 0, path: (path ? `${path}/` : '') + name, url: null, _opt: true }, ...prev]);
    try { await api.post(`/vault/${cliente}/folder`, { path, name }); success('Cartella creata'); }
    catch {} finally { load(); }
  };

  const removeEntry = async (en) => {
    if (!window.confirm(`Eliminare ${en.is_dir ? 'la cartella' : 'il file'} "${en.name}"${en.is_dir ? ' e tutto il contenuto' : ''}?`)) return;
    setEntries(prev => prev.filter(x => x.path !== en.path)); // optimistic
    try { await api.delete(`/vault/${cliente}`, { params: { path: en.path } }); }
    catch {} finally { load(); }
  };

  const clienteSel = clienti.find(c => c.id === cliente);
  const parts = path ? path.split('/') : [];
  const usedPct = Math.min(100, Math.round((usedBytes / MAX_BYTES) * 100));

  return (
    <div className="eden-page">
      <PageHeader title="Client Vault" icon={HardDrive}
        subtitle="Drive per cliente: carica cartelle e media, tutto in un posto"
        actions={
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 12, color: 'var(--text-muted)' }}>
            <HardDrive size={14} /> {fmtSize(usedBytes)} usati
          </div>
        } />

      {/* Selettore cliente */}
      <ClientePicker clienti={clienti} selected={cliente} onSelect={c => setCliente(c.id)} />
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', marginBottom: 16 }}>
        <div style={{ flex: 1 }} />
        <button onClick={creaStruttura} disabled={!cliente} title="Crea 01_Raw, 02_Selezioni, 03_Export, 04_Musiche, 05_Brand_kit, Produzione"
          style={btn('ghost')}><FolderTree size={15} /> Struttura standard</button>
        <button onClick={newFolder} disabled={!cliente} className="vault-btn"
          style={btn('ghost')}><FolderPlus size={15} /> Nuova cartella</button>
        <button onClick={() => fileInput.current?.click()} disabled={!cliente} style={btn('ghost')}>
          <Upload size={15} /> Carica file</button>
        <button onClick={() => dirInput.current?.click()} disabled={!cliente} style={btn('primary')}>
          <FolderUp size={15} /> Carica cartella</button>
        <input ref={fileInput} type="file" multiple hidden onChange={e => { doUpload(e.target.files); e.target.value = ''; }} />
        <input ref={dirInput} type="file" hidden webkitdirectory="" directory="" multiple onChange={e => { doUpload(e.target.files); e.target.value = ''; }} />
      </div>

      {/* Ricerca e tag */}
      {cliente && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 12 }}>
          <div style={{ position: 'relative', flex: '1 1 240px', maxWidth: 360 }}>
            <Search size={14} color="var(--text-dim)" style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)' }} />
            <input className="eden-input" value={cerca} onChange={e => setCerca(e.target.value)} placeholder="Cerca per nome, tag o nota…" style={{ paddingLeft: 30 }} />
          </div>
          {tagsCliente.slice(0, 14).map(t => (
            <button key={t.tag} onClick={() => setTagSel(s => s === t.tag ? '' : t.tag)}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11.5, padding: '4px 9px', borderRadius: 999, cursor: 'pointer',
                border: `1px solid ${tagSel === t.tag ? 'var(--cyan)' : 'var(--border)'}`, background: tagSel === t.tag ? 'var(--cyan-dim)' : 'var(--surface)',
                color: tagSel === t.tag ? 'var(--cyan)' : 'var(--text-muted)' }}>
              <Tag size={10} /> {t.tag} <span style={{ color: 'var(--text-dim)' }}>{t.n}</span>
            </button>
          ))}
        </div>
      )}

      {/* Spazio usato */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
        <div style={{ flex: 1, height: 6, background: 'var(--surface)', borderRadius: 999, overflow: 'hidden', border: '1px solid var(--border)' }}>
          <div style={{ width: `${usedPct}%`, height: '100%', background: usedPct > 90 ? 'var(--red)' : 'var(--cyan)', transition: 'width 0.3s' }} />
        </div>
        <span style={{ fontSize: 11, color: 'var(--text-dim)', whiteSpace: 'nowrap' }}>{fmtSize(usedBytes)} / 2 GB</span>
      </div>

      {/* Breadcrumb */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 12, flexWrap: 'wrap', fontSize: 13 }}>
        <button onClick={() => goTo(-1)} style={crumb(!path)}><HomeIcon size={13} /> {clienteSel ? (clienteSel.azienda || clienteSel.nome) : 'Home'}</button>
        {parts.map((p, i) => (
          <React.Fragment key={i}>
            <ChevronRight size={13} color="var(--text-dim)" />
            <button onClick={() => goTo(i)} style={crumb(i === parts.length - 1)}>{p}</button>
          </React.Fragment>
        ))}
      </div>

      {/* Upload in corso */}
      {uploads.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 14, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 12, padding: 12 }}>
          {uploads.slice(0, 8).map(u => (
            <div key={u.id} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              {u.status === 'uploading' ? <Loader2 size={14} className="eden-spin" color="var(--cyan)" />
                : u.status === 'error' ? <X size={14} color="var(--red)" /> : <div style={{ width: 14, height: 14, borderRadius: '50%', background: 'var(--green)' }} />}
              <span style={{ fontSize: 12, color: 'var(--text)', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{u.name}</span>
              <div style={{ width: 120, height: 5, background: 'var(--bg3)', borderRadius: 999, overflow: 'hidden' }}>
                <div style={{ width: `${u.pct}%`, height: '100%', background: u.status === 'error' ? 'var(--red)' : 'var(--cyan)', transition: 'width 0.2s' }} />
              </div>
              <span style={{ fontSize: 11, color: 'var(--text-dim)', width: 38, textAlign: 'right' }}>{u.status === 'error' ? 'err' : `${u.pct}%`}</span>
            </div>
          ))}
          {uploads.length > 8 && <div style={{ fontSize: 11, color: 'var(--text-dim)' }}>+{uploads.length - 8} altri…</div>}
        </div>
      )}

      {/* Area file (drop zone) */}
      <div
        onDragOver={e => { e.preventDefault(); if (!dragOver) setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        style={{
          border: `2px dashed ${dragOver ? 'var(--cyan)' : 'var(--border)'}`,
          background: dragOver ? 'var(--cyan-dim)' : 'transparent',
          borderRadius: 14, padding: 12, minHeight: 200, transition: 'background 0.15s, border-color 0.15s',
        }}>
        {!cliente ? (
          <EmptyState icon={HardDrive} title="Seleziona un cliente" hint="Scegli un cliente per aprire il suo vault." />
        ) : loading ? (
          <ListSkeleton rows={5} />
        ) : risultati && risultati.length === 0 ? (
          <EmptyState icon={Search} title="Nessun risultato" hint="Nessun file corrisponde alla ricerca o al tag selezionato." />
        ) : !risultati && entries.length === 0 ? (
          <EmptyState icon={Upload} title="Cartella vuota"
            hint="Trascina qui file e cartelle, oppure usa i pulsanti in alto. Limite 2 GB per file." />
        ) : (
          <div style={{ display: 'grid', gap: 10, gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))' }}>
            {(risultati || entries).map(en => (
              <div key={en.path} title={en.name}
                onDoubleClick={() => en.is_dir && enterFolder(en.name)}
                style={{ position: 'relative', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 12, overflow: 'hidden', cursor: en.is_dir ? 'pointer' : 'default', opacity: en._opt ? 0.6 : 1 }}>
                {/* Anteprima */}
                <div onClick={() => en.is_dir ? enterFolder(en.name) : (isImg(en.name) || isVid(en.name)) ? setPreview({ ...en, video: isVid(en.name) }) : null}
                  style={{ position: 'relative', height: 104, background: 'var(--bg3)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
                  {en.is_dir ? <Folder size={40} color="var(--cyan)" />
                    : isImg(en.name) ? <img src={en.url} alt="" loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    : isVid(en.name) && en.thumb_url ? <img src={en.thumb_url} alt="" loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    : isVid(en.name) ? <Film size={38} color="var(--purple)" />
                    : <FileIcon size={36} color="var(--text-dim)" />}
                  {isVid(en.name) && en.durata_sec != null && (
                    <span style={{ position: 'absolute', bottom: 5, right: 5, fontSize: 10, fontWeight: 700, color: '#fff', background: 'rgba(0,0,0,0.65)', padding: '1px 5px', borderRadius: 4 }}>
                      {fmtDurata(en.durata_sec)}{en.larghezza ? ` · ${en.larghezza > en.altezza ? '↔' : '↕'}` : ''}
                    </span>
                  )}
                </div>
                {/* Meta */}
                <div style={{ padding: '8px 10px' }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{en.name}</div>
                  <div style={{ fontSize: 10.5, color: 'var(--text-dim)', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {en.is_dir ? 'Cartella' : risultati ? en.path : fmtSize(en.size)}
                  </div>
                  {(en.tags || []).length > 0 && (
                    <div style={{ display: 'flex', gap: 3, flexWrap: 'wrap', marginTop: 4 }}>
                      {en.tags.slice(0, 3).map(t => <span key={t} style={{ fontSize: 9.5, padding: '0 5px', borderRadius: 4, background: 'var(--cyan-dim)', color: 'var(--cyan)' }}>{t}</span>)}
                      {en.tags.length > 3 && <span style={{ fontSize: 9.5, color: 'var(--text-dim)' }}>+{en.tags.length - 3}</span>}
                    </div>
                  )}
                </div>
                {/* Azioni */}
                <div style={{ position: 'absolute', top: 6, right: 6, display: 'flex', gap: 4 }}>
                  {!en.is_dir && en.url && (
                    <a href={en.url} download={en.name} onClick={e => e.stopPropagation()}
                      title="Scarica" style={iconBtn}><Download size={13} /></a>
                  )}
                  <button onClick={e => { e.stopPropagation(); removeEntry(en); }} title="Elimina" style={iconBtn}><Trash2 size={13} /></button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Preview media + scheda tecnica e tag */}
      {preview && (
        <div onClick={() => setPreview(null)} style={{ position: 'fixed', inset: 0, zIndex: 800, background: 'rgba(0,0,0,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 16, padding: 24, flexWrap: 'wrap', overflowY: 'auto' }}>
          <button onClick={() => setPreview(null)} style={{ position: 'absolute', top: 16, right: 20, background: 'none', border: 'none', color: '#fff', cursor: 'pointer' }}><X size={26} /></button>
          {preview.video
            ? <video src={preview.url} controls autoPlay style={{ maxWidth: 'min(92vw, 900px)', maxHeight: '80vh', borderRadius: 10 }} onClick={e => e.stopPropagation()} />
            : <img src={preview.url} alt={preview.name} style={{ maxWidth: 'min(92vw, 900px)', maxHeight: '80vh', objectFit: 'contain', borderRadius: 10 }} onClick={e => e.stopPropagation()} />}
          <InfoPanel cliente={cliente} entry={preview} onSaved={onTagsSaved} />
        </div>
      )}
    </div>
  );
}

/* ── Scheda tecnica + tag di un file ──────────────────────────────────── */
const FORMATI_CHECK = [['Reel', 'Instagram'], ['Storia', 'Instagram'], ['Post Singolo', 'Instagram'], ['Carosello', 'Instagram'], ['Video YT', 'YouTube'], ['Reel', 'TikTok']];

function InfoPanel({ cliente, entry, onSaved }) {
  const { success } = useToast();
  const [info, setInfo] = useState(null);
  const [check, setCheck] = useState('0');
  const [tags, setTags] = useState((entry.tags || []).join(', '));
  const [note, setNote] = useState(entry.note || '');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setInfo(null);
    const [formato, piattaforma] = FORMATI_CHECK[Number(check)];
    api.get(`/vault/${cliente}/info`, { params: { path: entry.path, formato, piattaforma }, silent: true })
      .then(r => setInfo(r.data)).catch(() => setInfo({ errore: true }));
  }, [cliente, entry.path, check]);

  const salva = async () => {
    setSaving(true);
    try {
      const r = await api.put(`/vault/${cliente}/tags`, { path: entry.path, tags: tags.split(',').map(t => t.trim()).filter(Boolean), note });
      success('Tag salvati'); onSaved({ ...entry, tags: r.data.tags, note: r.data.note });
    } catch { /* toast globale */ }
    setSaving(false);
  };

  const m = info?.meta;
  const riga = (k, v) => v ? <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, fontSize: 12.5 }}><span style={{ color: 'var(--text-dim)' }}>{k}</span><span style={{ color: 'var(--text)' }}>{v}</span></div> : null;

  return (
    <div onClick={e => e.stopPropagation()} style={{ width: 'min(300px, 92vw)', background: 'var(--bg2, #14141c)', border: '1px solid var(--border)', borderRadius: 12, padding: 14, display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)', wordBreak: 'break-all' }}>{entry.name}</div>
      {!info ? <div style={{ fontSize: 12, color: 'var(--text-dim)' }}>Analisi del file…</div> : info.errore ? <div style={{ fontSize: 12, color: 'var(--text-dim)' }}>Informazioni non disponibili</div> : (
        <>
          {riga('Durata', m.durata_sec != null ? fmtDurata(m.durata_sec) : null)}
          {riga('Risoluzione', m.larghezza ? `${m.larghezza}×${m.altezza}` : null)}
          {riga('Formato', m.ratio)}
          {riga('FPS', m.fps)}
          {riga('Codec', m.codec)}
          {riga('Peso', fmtSize(m.peso))}
          <div style={{ borderTop: '1px solid var(--border)', paddingTop: 8, marginTop: 2 }}>
            <select className="eden-input" value={check} onChange={e => setCheck(e.target.value)} style={{ fontSize: 12 }}>
              {FORMATI_CHECK.map(([f, p], i) => <option key={i} value={i}>Va bene per {f} {p}?</option>)}
            </select>
            <div style={{ marginTop: 6, display: 'flex', flexDirection: 'column', gap: 3 }}>
              {(info.problemi || []).length === 0
                ? <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12, color: 'var(--green)' }}><CheckCircle2 size={13} /> Conforme</span>
                : info.problemi.map((p, i) => (
                  <span key={i} style={{ display: 'flex', gap: 5, fontSize: 12, color: p.livello === 'blocco' ? 'var(--red)' : 'var(--yellow)' }}><AlertTriangle size={13} style={{ flexShrink: 0, marginTop: 1 }} /> {p.testo}</span>
                ))}
            </div>
          </div>
        </>
      )}
      <div style={{ borderTop: '1px solid var(--border)', paddingTop: 8 }}>
        <div style={{ fontSize: 11.5, color: 'var(--text-muted)', marginBottom: 4 }}>Tag (separati da virgola)</div>
        <input className="eden-input" value={tags} onChange={e => setTags(e.target.value)} placeholder="b-roll, prodotto, esterno, Mario" />
        <div style={{ fontSize: 11.5, color: 'var(--text-muted)', margin: '8px 0 4px' }}>Nota</div>
        <textarea className="eden-input" value={note} onChange={e => setNote(e.target.value)} style={{ minHeight: 50, resize: 'vertical' }} />
        <button onClick={salva} disabled={saving} className="eden-btn-primary" style={{ marginTop: 8, fontSize: 12, width: '100%', justifyContent: 'center' }}>
          <Tag size={13} /> {saving ? 'Salvo…' : 'Salva tag'}
        </button>
      </div>
    </div>
  );
}

/* ── stili helper ─────────────────────────────────────────────────────── */
const btn = (variant) => ({
  display: 'flex', alignItems: 'center', gap: 6, fontSize: 12.5, fontWeight: 600,
  padding: '0 14px', height: 38, borderRadius: 9, cursor: 'pointer',
  border: variant === 'primary' ? 'none' : '1px solid var(--border)',
  background: variant === 'primary' ? 'var(--cyan)' : 'var(--surface)',
  color: variant === 'primary' ? 'var(--on-accent)' : 'var(--text-muted)',
});
const crumb = (active) => ({
  display: 'flex', alignItems: 'center', gap: 4, fontSize: 12.5, fontWeight: active ? 700 : 500,
  padding: '4px 8px', borderRadius: 7, border: 'none', cursor: 'pointer',
  background: active ? 'var(--cyan-dim)' : 'transparent', color: active ? 'var(--cyan)' : 'var(--text-muted)',
});
const iconBtn = {
  display: 'flex', alignItems: 'center', justifyContent: 'center', width: 26, height: 26,
  borderRadius: 7, border: 'none', cursor: 'pointer', textDecoration: 'none',
  background: 'rgba(10,10,18,0.6)', color: '#fff', backdropFilter: 'blur(4px)',
};
